import React, { FunctionComponent } from 'react'
import styled from 'styled-components/native'

import { useHandleFocus } from 'libs/hooks/useHandleFocus'
import { useHandleHover } from 'libs/hooks/useHandleHover'
import { useMobileFontScaleToDisplay } from 'shared/accessibility/helpers/zoomHelpers'
import { ColorsType } from 'theme/types'
import { InternalTouchableLink } from 'ui/components/touchableLink/InternalTouchableLink'
import {
  InternalNavigationProps,
  InternalTouchableLinkProps,
} from 'ui/components/touchableLink/types'
import { Typo, getSpacing } from 'ui/theme'
import { customFocusOutline } from 'ui/theme/customFocusOutline/customFocusOutline'
import { getHoverStyle } from 'ui/theme/getHoverStyle/getHoverStyle'
import { setTextSemantic } from 'ui/theme/typographyAttrs/setTextSemantic'

export type CategoryButtonProps = {
  label: string
  navigateTo: InternalNavigationProps['navigateTo']
  onBeforeNavigate?: () => void
  height?: number
  style?: InternalTouchableLinkProps['style']
  fillColor: ColorsType
  borderColor: ColorsType
}
export const CategoryButton: FunctionComponent<CategoryButtonProps> = ({
  label,
  fillColor,
  borderColor,
  style,
  navigateTo,
  onBeforeNavigate,
  height,
}) => {
  const effectiveHeight = useMobileFontScaleToDisplay({
    default: height,
    at200PercentZoom: undefined,
  })

  const focusProps = useHandleFocus()
  const hoverProps = useHandleHover()

  return (
    <TouchableContainer
      {...focusProps}
      {...hoverProps}
      onMouseDown={(e: Event) => e.preventDefault()} // Prevent focus on click
      navigateTo={navigateTo}
      onBeforeNavigate={onBeforeNavigate}
      accessibilityLabel={`Catégorie ${label}`}
      baseColor={fillColor}
      borderColor={borderColor}
      style={style}
      height={effectiveHeight}>
      <Label {...setTextSemantic('span')}>{label.toUpperCase()}</Label>
    </TouchableContainer>
  )
}

const MIN_HEIGHT = getSpacing(20)

const TouchableContainer: typeof InternalTouchableLink = styled(InternalTouchableLink)<{
  onMouseDown: (e: Event) => void
  isFocus: boolean
  isHover: boolean
  baseColor: ColorsType
  borderColor: ColorsType
  height?: number
}>(({ theme, isFocus, isHover, baseColor, borderColor, height }) => ({
  height: height ?? undefined,
  minHeight: MIN_HEIGHT,
  overflow: 'hidden',
  borderRadius: theme.designSystem.size.borderRadius.m,
  ...customFocusOutline({ theme, isFocus }),
  ...getHoverStyle({ underlineColor: theme.designSystem.color.text.default, isHover }),
  backgroundColor: baseColor,
  borderColor,
  borderWidth: '1.6px',
  justifyContent: height ? 'flex-end' : undefined,
  padding: theme.designSystem.size.spacing.s,
}))

const Label = styled(Typo.BodyAccentS).attrs({ numberOfLines: 4 })(({ theme }) => ({
  textAlign: 'left',
  color: theme.designSystem.color.text.default,
}))
